import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { AGENT_NAV } from "@/lib/agent-nav";
import { PortalPage } from "@/components/portal/PortalPage";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { BookOpen } from "lucide-react";

interface ResourceRow {
  id: string;
  title: string;
  slug: string;
  summary: string | null;
  category: string | null;
  cover_url: string | null;
  updated_at: string;
}

export const Route = createFileRoute("/agent/resources")({
  head: () => ({ meta: [{ title: "Resources — Strategist Portal" }, { name: "robots", content: "noindex" }] }),
  component: () => (
    <ProtectedRoute requiredRole="agent">
      <PortalShell title="Strategist Portal" nav={AGENT_NAV} tourKey="agent_tour">
        <AgentResources />
      </PortalShell>
    </ProtectedRoute>
  ),
});

function AgentResources() {
  const [rows, setRows] = useState<ResourceRow[]>([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    void loadResources();
  }, []);

  async function loadResources() {
    const { data } = await supabase
      .from("academy_resources")
      .select("id, title, slug, summary, category, cover_url, updated_at")
      .eq("published", true)
      .in("audience", ["agent", "all"])
      .order("updated_at", { ascending: false });
    setRows(data ?? []);
  }

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((row) => [row.title, row.summary, row.category].some((value) => (value ?? "").toLowerCase().includes(needle)));
  }, [q, rows]);

  return (
    <PortalPage eyebrow="Sales Vault" title="Resources" intro="Strategist-facing published resources pulled from the Academy resource library.">
      <Input placeholder="Search resources" value={q} onChange={(event) => setQ(event.target.value)} className="max-w-sm mb-6 bg-white" />
      {rows.length === 0 ? (
        <div className="bg-cream-50 border border-bronze-600/30 rounded-[4px] p-10 text-center">
          <BookOpen className="mx-auto h-8 w-8 text-bronze-600" />
          <h3 className="mt-4 font-display text-navy-800 text-[22px]">No strategist resources published</h3>
          <p className="mt-2 font-sans text-[14px] text-charcoal-500">Admin-published strategist resources appear here when available.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {filtered.map((row) => (
            <article key={row.id} className="bg-white border border-bronze-600/20 rounded-[4px] p-5 shadow-soft">
              <div className="flex items-start gap-4">
                {row.cover_url ? <img src={row.cover_url} alt="" className="h-16 w-16 rounded-[4px] object-cover" /> : <div className="h-16 w-16 rounded-[4px] bg-cream-50 border border-bronze-600/20 flex items-center justify-center"><BookOpen className="h-5 w-5 text-bronze-600" /></div>}
                <div className="min-w-0 flex-1">
                  <p className="font-sans text-[10px] uppercase tracking-[0.18em] text-bronze-600">{row.category ?? "Resource"}</p>
                  <h3 className="mt-1 font-display text-navy-800 text-[20px]">{row.title}</h3>
                  {row.summary && <p className="mt-2 font-sans text-[13px] leading-relaxed text-charcoal-500">{row.summary}</p>}
                  <a href={`/academy/library?resource=${row.slug}`} className="mt-4 inline-block font-sans text-[11px] font-semibold uppercase tracking-[0.18em] text-bronze-600 hover:text-navy-800">Open resource →</a>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </PortalPage>
  );
}